import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

export default function Home() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAllReasons, setShowAllReasons] = useState(false);

  useEffect(() => {
    fetch('https://api.dev.auxo.dk/public/content/services')
      .then((res) => res.json())
      .then((data) => {
        // Sort services to match desired order: TIG, CO2, Industriservice, then others
        const sorted = (data || []).sort((a, b) => {
          const order = ['tig-svejsning', 'co2-mig-mag', 'industriservice', 'svejsning-i-aluminium'];
          return order.indexOf(a.key) - order.indexOf(b.key);
        });
        setServices(sorted);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Error fetching services:', err);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('active');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = document.querySelectorAll('.reveal');
    elements.forEach((el) => observer.observe(el));

    return () => {
      elements.forEach((el) => observer.unobserve(el));
    };
  }, [services, showAllReasons, loading]);

  const usps = [
    { icon: 'fa-solid fa-truck-fast', text: 'Hurtig udrykning' },
    { icon: 'fa-solid fa-award', text: 'Mange års erfaring' },
    { icon: 'fa-solid fa-fire-burner', text: 'TIG & MIG/MAG' },
    { icon: 'fa-solid fa-users', text: 'Industri og private' },
  ];

  const reasons = [
    {
      icon: 'fa-solid fa-bullseye',
      title: 'Høj præcision',
      desc: 'Vi leverer svejsninger med fokus på kvalitet og detaljer, hvor styrke og æstetik går hånd i hånd.',
    },
    {
      icon: 'fa-solid fa-reply',
      title: 'Hurtig respons',
      desc: 'Vi forstår vigtigheden af hurtig afklaring og rykker hurtigt ud, når der er brug for akut hjælp.',
    },
    {
      icon: 'fa-solid fa-sliders',
      title: 'Fleksible løsninger',
      desc: 'Vi tilpasser os dine behov og tidsplaner, uanset om det er en mindre reparation eller et stort industriprojekt.',
    },
    {
      icon: 'fa-solid fa-shield-halved',
      title: 'Fokus på kvalitet',
      desc: 'Alt arbejde udføres efter gældende standarder og kvalitetskrav, så du får et holdbart resultat.',
    },
    {
      icon: 'fa-solid fa-handshake',
      title: 'Personlig service',
      desc: 'Du møder engagerede fagfolk, der tager ansvar for opgaven og rådgiver dig hele vejen igennem.',
    },
    {
      icon: 'fa-solid fa-gears',
      title: 'Moderne udstyr',
      desc: 'Vi råder over moderne svejseudstyr og værktøjer, hvilket sikrer den mest effektive og præcise udførelse af dit projekt.',
    },
  ];

  return (
    <div className="space-y-24 pb-24">
      {/* Hero Section */}
      <section className="relative overflow-hidden min-h-[650px] lg:min-h-[750px] flex items-center bg-blue-950">
        {/* Full-bleed Background Image */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1920,quality=85,format=auto/assets/898ac264-e288-4855-90ba-a9f48bcc4333/danish-male-welder-hero.jpg"
            alt="Professionel præcisionssvejsning af erfaren svejser"
            className="h-full w-full object-cover object-center lg:object-right"
          />
          {/* Dark overlay to ensure text and form readability */}
          <div className="absolute inset-0 bg-blue-950/80 lg:bg-transparent lg:bg-gradient-to-r lg:from-blue-950 lg:via-blue-950/80 lg:to-transparent" />
        </div>

        <div className="mx-auto max-w-7xl w-full px-4 sm:px-6 lg:px-8 py-16 lg:py-24 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Left Content */}
            <div className="lg:col-span-7 space-y-6 text-left">
              <h1 className="text-2xl font-extrabold tracking-tight text-white sm:text-4xl lg:text-5xl leading-tight uppercase">
                Din specialist i<br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-sky-400 to-blue-300">præcisionssvejsning</span>
              </h1>

              <p className="text-base sm:text-lg text-slate-200 leading-relaxed max-w-xl">
                JC Svejseteknik leverer svejsearbejde i højeste kvalitet til både industri og private. Vi sætter en ære i det gode, solide håndværk og den personlige service.
              </p>

              <div className="pt-4">
                <a
                  href="tel:+4561167778"
                  className="inline-flex items-center gap-3 bg-brand-accent px-8 py-4 text-base font-bold tracking-wider text-white transition-all hover:bg-brand-accent/90 hover:shadow-lg hover:shadow-brand-accent/20 whitespace-nowrap"
                >
                  <i className="fa-solid fa-phone" aria-hidden="true" />
                  +45 61 16 77 78
                </a>
              </div>
            </div>

            {/* Right Webform Card */}
            <div className="lg:col-span-5">
              <div className="rounded-2xl border border-slate-100 bg-white p-6 sm:p-8 shadow-2xl text-slate-900 relative">
                <h3 className="text-lg font-extrabold text-slate-900 mb-2 uppercase tracking-tight">
                  Få et uforpligtende tilbud
                </h3>
                <p className="text-xs text-slate-500 mb-6">
                  Udfyld formularen, så vender vi tilbage til dig hurtigst muligt.
                </p>
                <HeroContactForm />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Presentation Section */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 reveal">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-2 lg:items-center">
          {/* Left Image */}
          <div className="relative order-2 lg:order-1">
            <img
              src="https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=85,format=auto/assets/898ac264-e288-4855-90ba-a9f48bcc4333/jc-svejseteknik-team.jpg"
              alt="JC Svejseteknik Team"
              className="w-full rounded-2xl border border-slate-200 object-cover shadow-2xl aspect-[4/3]"
            />
          </div>

          {/* Right Content */}
          <div className="space-y-6 order-1 lg:order-2">
            <h2 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl uppercase">
              Specialiseret præcisionsarbejde til både private og virksomheder
            </h2>
            <div className="text-slate-600 space-y-4 leading-relaxed">
              <p>
                Når du har brug for et flot, stærkt og præcist resultat, er TIG svejsning en oplagt løsning. Hos JC Svejseteknik udfører vi CO2- og TIG-svejsning for både private og erhverv i Brande, Silkeborg, Horsens samt i resten af Danmark og udlandet.
              </p>
            </div>
            <div className="pt-4">
              <Link
                to="/om-os"
                className="inline-flex items-center gap-2 text-sm font-semibold tracking-wider text-slate-950 hover:text-slate-700 transition-colors group"
              >
                Læs mere om os
                <i className="fa-solid fa-arrow-right transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="bg-brand-gray py-12 border-y border-slate-100 reveal">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-8 space-y-3">
            <h2 className="text-xl font-bold tracking-tight text-slate-900 sm:text-3xl uppercase">
              Derfor vælger kunder JC Svejseteknik
            </h2>
            <p className="text-slate-500 text-sm">
              Vi sætter en ære i at levere stærke og flotte resultater – hver gang. Her er grundene til, at vores kunder samarbejder med os år efter år.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {reasons.slice(0, showAllReasons ? reasons.length : 3).map((reason, idx) => (
              <div
                key={idx}
                className={`p-6 transition-all ${
                  idx === 0
                    ? 'bg-brand-accent text-white border border-brand-accent shadow-md'
                    : 'border border-slate-100 bg-white'
                }`}
              >
                <div className={`text-xl mb-4 ${idx === 0 ? 'text-white' : 'text-slate-950'}`}>
                  <i className={reason.icon} />
                </div>
                <h3 className={`text-base font-bold mb-2 uppercase ${idx === 0 ? 'text-white' : 'text-slate-900'}`}>
                  {reason.title}
                </h3>
                <p className={`text-sm leading-relaxed ${idx === 0 ? 'text-sky-100' : 'text-slate-500'}`}>
                  {reason.desc}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-8 text-center">
            <button
              onClick={() => setShowAllReasons(!showAllReasons)}
              className="inline-flex items-center gap-2 border border-slate-200 bg-white px-5 py-2.5 text-xs font-semibold tracking-wider text-slate-800 transition-all hover:bg-slate-50 hover:border-brand-accent/30 cursor-pointer"
            >
              {showAllReasons ? 'Vis færre' : 'Vis flere fordele'}
              <i className={`fa-solid ${showAllReasons ? 'fa-chevron-up' : 'fa-chevron-down'} text-brand-accent`} />
            </button>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 reveal">
        <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
          <h2 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl uppercase">
            Professionelle svejseydelser
          </h2>
          <p className="text-slate-500">
            Vi tilbyder specialiseret svejsearbejde til industri, erhverv og private. Klik på en ydelse for at læse mere.
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-accent border-t-transparent" />
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
            {services.filter(s => s.key !== 'svejsning-i-aluminium').map((service) => (
              <div
                key={service.key}
                className="group flex flex-col justify-between rounded-xl border border-slate-200 bg-white overflow-hidden transition-all hover:border-brand-accent/30 hover:shadow-md"
              >
                <div>
                  <div className="relative overflow-hidden aspect-[16/10]">
                    <img
                      src={service.image?.[0] ? `${service.image[0]}?width=400&quality=80&format=auto` : ''}
                      alt={service.title?.da}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute top-4 left-4 flex h-10 w-10 items-center justify-center rounded-lg bg-slate-950/80 text-brand-accent text-lg border border-white/10">
                      <i className={service.icon} />
                    </div>
                  </div>
                  <div className="p-6 space-y-3">
                    <h3 className="text-xl font-bold text-slate-900 group-hover:text-brand-accent transition-colors uppercase">
                      {service.title?.da}
                    </h3>
                    <p className="text-sm text-slate-500 leading-relaxed line-clamp-3">
                      {service.intro?.da}
                    </p>
                  </div>
                </div>
                <div className="p-6 pt-0">
                  <Link
                    to={`/ydelser/${service.key}`}
                    className="inline-flex items-center gap-2 text-sm font-semibold tracking-wider text-slate-950 group-hover:translate-x-1 transition-transform"
                  >
                    Læs mere
                    <i className="fa-solid fa-arrow-right" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Large CTA Section */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 reveal">
        <div className="relative rounded-2xl border border-slate-800 bg-blue-950 overflow-hidden p-6 sm:p-12 lg:p-16 text-center">
          <div className="absolute inset-0 opacity-[0.08]" style={{ backgroundImage: 'radial-gradient(circle, #3b82f6 1px, transparent 1px)', backgroundSize: '24px 24px' }} />
          <div className="absolute -top-24 -right-24 h-48 w-48 rounded-full bg-brand-accent/10 blur-2xl" />
          <div className="absolute -bottom-24 -left-24 h-48 w-48 rounded-full bg-brand-orange/5 blur-2xl" />

          <div className="relative z-10 max-w-3xl mx-auto space-y-4 sm:space-y-6">
            <h2 className="text-lg sm:text-3xl font-bold tracking-tight text-white uppercase">
              Har du et projekt, der kræver professionel svejsning?
            </h2>
            <p className="text-slate-300 text-sm sm:text-lg">
              Vi står klar til at hjælpe dig med præcise løsninger, høj kvalitet og hurtig levering. Kontakt os i dag for en uforpligtende snak og et skræddersyet tilbud.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-3 pt-2 sm:pt-4">
              <Link
                to="/kontakt"
                className="rounded-md bg-brand-accent px-5 py-3 text-sm sm:px-8 sm:py-4 sm:text-base font-semibold tracking-wider text-white transition-all hover:bg-brand-accent/80 hover:shadow-lg hover:shadow-brand-accent/20"
              >
                Indhent et tilbud
              </Link>
              <a
                href="tel:+4561167778"
                className="inline-flex items-center justify-center gap-3 rounded-md border-2 border-white bg-transparent px-5 py-3 text-sm sm:px-8 sm:py-4 sm:text-base font-semibold tracking-wider text-white transition-all hover:bg-white/10 whitespace-nowrap"
              >
                <i className="fa-solid fa-phone" />
                <span className="whitespace-nowrap">+45 61 16 77 78</span>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function HeroContactForm() {
  const [status, setStatus] = useState('idle'); // idle, loading, success, error
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    setErrorMessage('');

    const formData = new FormData(e.target);
    const data = Object.fromEntries(formData.entries());

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data),
      });

      if (response.ok) {
        setStatus('success');
        e.target.reset();
      } else {
        const errData = await response.json().catch(() => ({}));
        setErrorMessage(errData.message || 'Der opstod en fejl ved afsendelse. Prøv igen.');
        setStatus('error');
      }
    } catch (err) {
      console.error('Form submission error:', err);
      setErrorMessage('Netværksfejl. Kontroller din internetforbindelse og prøv igen.');
      setStatus('error');
    }
  };

  if (status === 'success') {
    return (
      <div className="rounded-xl bg-emerald-500/10 border border-emerald-500/20 p-6 text-center space-y-4">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-500/20 text-emerald-600 mx-auto">
          <i className="fa-solid fa-circle-check text-2xl" />
        </div>
        <h4 className="text-lg font-bold text-slate-900">Beskeden er sendt!</h4>
        <p className="text-sm text-slate-600">
          Tak for din henvendelse. Vi vender tilbage til dig hurtigst muligt.
        </p>
        <button
          onClick={() => setStatus('idle')}
          className="text-xs font-semibold uppercase tracking-wider text-brand-accent hover:underline"
        >
          Send en ny besked
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-3.5">
      {/* Antispam Honeypot Field */}
      <div className="sr-only absolute">
        <label htmlFor="hero_website_url">Please leave this field empty</label>
        <input type="text" id="hero_website_url" name="website_url" tabIndex="-1" autoComplete="off" />
      </div>

      <div>
        <label htmlFor="hero_name" className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">
          Navn <span className="text-brand-accent">*</span>
        </label>
        <input
          type="text"
          id="hero_name"
          name="name"
          required
          placeholder="Dit fulde navn"
          className="w-full rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:border-brand-accent focus:bg-white focus:outline-none focus:ring-1 focus:ring-brand-accent"
        />
      </div>

      <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2">
        <div>
          <label htmlFor="hero_phone" className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">
            Telefonnummer <span className="text-brand-accent">*</span>
          </label>
          <input
            type="tel"
            id="hero_phone"
            name="phone"
            required
            placeholder="F.eks. 61 16 77 78"
            className="w-full rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:border-brand-accent focus:bg-white focus:outline-none focus:ring-1 focus:ring-brand-accent"
          />
        </div>
        <div>
          <label htmlFor="hero_email" className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">
            E-mail <span className="text-brand-accent">*</span>
          </label>
          <input
            type="email"
            id="hero_email"
            name="email"
            required
            placeholder="din@email.dk"
            className="w-full rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:border-brand-accent focus:bg-white focus:outline-none focus:ring-1 focus:ring-brand-accent"
          />
        </div>
      </div>

      <div>
        <label htmlFor="hero_message" className="block text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">
          Beskrivelse <span className="text-brand-accent">*</span>
        </label>
        <textarea
          id="hero_message"
          name="message"
          required
          rows="3"
          placeholder="Beskriv din opgave..."
          className="w-full rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:border-brand-accent focus:bg-white focus:outline-none focus:ring-1 focus:ring-brand-accent"
        ></textarea>
      </div>

      {status === 'error' && (
        <div className="rounded-md bg-brand-orange/10 border border-brand-orange/20 p-2 text-xs text-brand-orange">
          <i className="fa-solid fa-circle-exclamation mr-2" />
          {errorMessage}
        </div>
      )}

      <button
        type="submit"
        disabled={status === 'loading'}
        className="w-full rounded-md bg-brand-accent py-3 text-xs font-bold tracking-widest text-white transition-all hover:bg-brand-accent/90 disabled:opacity-50"
      >
        {status === 'loading' ? (
          <span className="flex items-center justify-center gap-2">
            <i className="fa-solid fa-spinner animate-spin" />
            Sender...
          </span>
        ) : (
          'Få et tilbud'
        )}
      </button>
    </form>
  );
}
